"use client";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import React from "react";

// ============================================================================
// Constants
// ============================================================================

const API_BASE = "https://api.coldx.io/api/v1";

const STEPS = [1, 2, 3, 4] as const;
type StepKey = (typeof STEPS)[number];

type StepDef = {
  method: "POST" | "GET" | "PATCH";
  path: string;
  scope: string;
  request: string;
  response: string;
};

const STEP_DEFS: Record<StepKey, StepDef> = {
  1: {
    method: "POST",
    path: "/leads/import",
    scope: "leads.import",
    request: `curl -X POST ${API_BASE}/leads/import \\
  -H "Authorization: Bearer cf_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "leads": [{
      "company_name": "Acme GmbH",
      "website": "acme.de",
      "zip": "10115", "city": "Berlin",
      "contacts": [{
        "firstname": "Sara", "lastname": "Meier",
        "email": "sara@acme.de", "phone": "+49301234567"
      }],
      "custom_fields": { "42": "ext-123" }
    }]
  }'`,
    response: `{
  "job_id": 123,
  "status": "pending",
  "total": 1,
  "duplicates_detected": 0
}`,
  },
  2: {
    method: "GET",
    path: "/leads/import/:job_id",
    scope: "leads.import",
    request: `curl ${API_BASE}/leads/import/123 \\
  -H "Authorization: Bearer cf_your_api_key"`,
    response: `{
  "job_id": 123,
  "status": "completed",
  "total_rows": 1,
  "processed_rows": 1,
  "success_count": 1,
  "merge_count": 0,
  "error_count": 0,
  "completed_at": "2026-09-08T14:02:11.000Z"
}`,
  },
  3: {
    method: "GET",
    path: "/leads?cf[42]=ext-123",
    scope: "leads.read",
    request: `curl "${API_BASE}/leads?cf[42]=ext-123&limit=1" \\
  -H "Authorization: Bearer cf_your_api_key"`,
    response: `{
  "count": 1,
  "leads": [{
    "id": 4711,
    "company_name": "Acme GmbH",
    "website": "acme.de",
    "custom_fields": { "42": "ext-123" },
    "contacts": [{
      "id": 88, "firstname": "Sara", "lastname": "Meier",
      "emails": ["sara@acme.de"], "phones": ["+49301234567"]
    }]
  }]
}`,
  },
  4: {
    method: "PATCH",
    path: "/leads/:id",
    scope: "leads.update",
    request: `curl -X PATCH ${API_BASE}/leads/4711 \\
  -H "Authorization: Bearer cf_your_api_key" \\
  -H "Content-Type: application/json" \\
  -d '{
    "custom_fields": { "58": "13" },
    "contacts": [
      { "id": 88, "position": "CEO" },
      { "firstname": "Tom", "lastname": "Berg",
        "emails": ["tom@acme.de"] }
    ]
  }'`,
    response: `{
  "lead_id": 4711,
  "updated": { "custom_fields": 1, "contacts": 2 },
  "updated_at": "2026-09-08T14:03:40.000Z"
}`,
  },
};

const ROTATION_MS = 7000;

const METHOD_STYLES: Record<StepDef["method"], string> = {
  POST: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  GET: "bg-sky-500/15 text-sky-300 ring-sky-500/30",
  PATCH: "bg-amber-500/15 text-amber-300 ring-amber-500/30",
};

// ============================================================================
// Root Section
// ============================================================================

export const ApiSection = () => {
  return (
    <section className="dark relative w-full overflow-hidden bg-neutral-950">
      <SectionBackground />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-36">
        <BrandReveal />
        <div className="mt-12 lg:mt-20">
          <ApiScene />
        </div>
        <div className="mt-14 lg:mt-24">
          <CapabilityGrid />
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Background — dark band with hairlines
// ============================================================================

const SectionBackground = () => (
  <>
    <div className="absolute inset-x-0 top-0 h-px bg-neutral-800" />
    <div className="absolute inset-x-0 bottom-0 h-px bg-neutral-800" />
  </>
);

// ============================================================================
// Block 1 — Brand Reveal
// ============================================================================

const BrandReveal = () => {
  const t = useTranslations("api");
  return (
    <div className="flex flex-col items-center text-center">
      <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500">
        <CodeIcon className="h-3.5 w-3.5" />
        API
      </p>

      <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white max-w-3xl">
        {t("tagline")}
      </h2>

      <p className="mt-5 text-sm md:text-base text-neutral-400 max-w-2xl leading-relaxed">
        {t("lead")}
      </p>
    </div>
  );
};

// ============================================================================
// Block 2 — API Scene
// ============================================================================

const ApiScene = () => {
  const t = useTranslations("api");
  const [active, setActive] = React.useState<StepKey>(1);

  React.useEffect(() => {
    const id = setInterval(() => {
      setActive((prev) => {
        const idx = STEPS.indexOf(prev);
        return STEPS[(idx + 1) % STEPS.length];
      });
    }, ROTATION_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[560px]">
        <StepRail active={active} onSelect={setActive} />
        <RequestPanel active={active} />
      </div>
      <div className="border-t border-neutral-800 bg-neutral-900/40 px-5 py-3 flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        <span className="text-[10px] text-neutral-500">{t("footer_meta")}</span>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Step rail (left)
// ----------------------------------------------------------------------------

const StepRail = ({
  active,
  onSelect,
}: {
  active: StepKey;
  onSelect: (k: StepKey) => void;
}) => {
  const t = useTranslations("api");
  return (
    <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-neutral-800 bg-neutral-900/30 p-5 md:p-6 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-red-400">
          {t("rail_label")}
        </span>
        <span className="text-[10px] text-neutral-500">{t("rail_meta")}</span>
      </div>

      <div className="space-y-2">
        {STEPS.map((k) => {
          const def = STEP_DEFS[k];
          const isActive = k === active;
          return (
            <button
              key={k}
              type="button"
              onClick={() => onSelect(k)}
              className={cn(
                "w-full text-left rounded-xl border px-4 py-3 transition-all duration-300",
                isActive
                  ? "border-red-500/50 bg-red-500/10"
                  : "border-neutral-800 bg-neutral-900/40 hover:border-neutral-700"
              )}
            >
              <div className="flex items-center gap-2.5">
                <span
                  className={cn(
                    "inline-flex h-6 w-6 items-center justify-center rounded-md text-[11px] font-bold tabular-nums",
                    isActive
                      ? "bg-red-500 text-white"
                      : "bg-neutral-800 text-neutral-400"
                  )}
                >
                  {k}
                </span>
                <span
                  className={cn(
                    "text-sm font-semibold",
                    isActive ? "text-white" : "text-neutral-300"
                  )}
                >
                  {t(`step_${k}_label`)}
                </span>
              </div>
              <div className="mt-1.5 flex items-center gap-2 pl-[34px]">
                <MethodBadge method={def.method} />
                <span className="font-mono text-[11px] text-neutral-500 truncate">
                  {def.path}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-auto pt-6">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500 mb-2">
          {t("scopes_label")}
        </div>
        <div className="flex flex-wrap gap-1.5">
          {(["leads.import", "leads.read", "leads.update"] as const).map(
            (scope) => {
              const isActive = STEP_DEFS[active].scope === scope;
              return (
                <span
                  key={scope}
                  className={cn(
                    "inline-flex items-center rounded border px-2 py-1 font-mono text-[11px] transition-colors duration-300",
                    isActive
                      ? "border-red-500/40 bg-red-500/10 text-red-300"
                      : "border-neutral-800 bg-neutral-900/70 text-neutral-500"
                  )}
                >
                  {scope}
                </span>
              );
            }
          )}
        </div>
      </div>
    </div>
  );
};

const MethodBadge = ({ method }: { method: StepDef["method"] }) => (
  <span
    className={cn(
      "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wider ring-1",
      METHOD_STYLES[method]
    )}
  >
    {method}
  </span>
);

// ----------------------------------------------------------------------------
// Request / response panel (right)
// ----------------------------------------------------------------------------

const RequestPanel = ({ active }: { active: StepKey }) => {
  const t = useTranslations("api");
  const def = STEP_DEFS[active];
  return (
    <div className="lg:col-span-8 p-5 md:p-6 flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MethodBadge method={def.method} />
          <span className="font-mono text-xs text-neutral-300">
            {API_BASE}
            {def.path}
          </span>
        </div>
        <span className="text-[10px] text-neutral-500">{t("panel_meta")}</span>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={active}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.3 }}
          className="flex-1 grid grid-cols-1 xl:grid-cols-2 gap-4"
        >
          <CodeCard label={t("request_label")} code={def.request} />
          <CodeCard label={t("response_label")} code={def.response} accent />
        </motion.div>
      </AnimatePresence>

      <p className="mt-4 text-xs text-neutral-500 leading-relaxed">
        {t(`step_${active}_note`)}
      </p>
    </div>
  );
};

const CodeCard = ({
  label,
  code,
  accent = false,
}: {
  label: string;
  code: string;
  accent?: boolean;
}) => (
  <div
    className={cn(
      "rounded-xl border overflow-hidden flex flex-col",
      accent
        ? "border-red-500/25 bg-red-950/10"
        : "border-neutral-800 bg-neutral-900/40"
    )}
  >
    <div className="flex items-center gap-2 px-3 py-2 border-b border-neutral-800/80">
      <span className="h-2 w-2 rounded-full bg-neutral-700" />
      <span className="h-2 w-2 rounded-full bg-neutral-700" />
      <span className="h-2 w-2 rounded-full bg-neutral-700" />
      <span className="ml-2 text-[10px] uppercase tracking-wider font-semibold text-neutral-500">
        {label}
      </span>
    </div>
    <pre className="flex-1 overflow-x-auto p-3 text-[11px] md:text-xs font-mono leading-relaxed text-neutral-300">
      <code>{code}</code>
    </pre>
  </div>
);

// ============================================================================
// Block 3 — Capability Grid
// ============================================================================

type TileConfig = { key: string; icon: React.ReactNode };

const TILES: TileConfig[] = [
  { key: "t1", icon: <IconKey /> },
  { key: "t2", icon: <IconImport /> },
  { key: "t3", icon: <IconSync /> },
  { key: "t4", icon: <IconWebhook /> },
];

const CapabilityGrid = () => {
  const t = useTranslations("api");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {TILES.map((tile) => (
        <CapabilityTile
          key={tile.key}
          icon={tile.icon}
          label={t(`${tile.key}_label`)}
          desc={t(`${tile.key}_desc`)}
        />
      ))}
    </div>
  );
};

const CapabilityTile = ({
  icon,
  label,
  desc,
}: {
  icon: React.ReactNode;
  label: string;
  desc: string;
}) => (
  <div className="relative rounded-xl border border-neutral-800 bg-neutral-900/50 p-5">
    <div className="relative">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-800 text-red-500">
        {icon}
      </div>
      <h3 className="mt-3 text-sm md:text-base font-semibold text-white">
        {label}
      </h3>
      <p className="mt-1 text-xs md:text-sm text-neutral-400 leading-relaxed">
        {desc}
      </p>
    </div>
  </div>
);

// ============================================================================
// Icons
// ============================================================================

function CodeIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="m8 7-5 5 5 5M16 7l5 5-5 5M14 4l-4 16" />
    </svg>
  );
}

function IconKey() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <circle cx="8" cy="14" r="4" />
      <path d="m11 11 9-9M17 5l2 2M14 8l2 2" />
    </svg>
  );
}

function IconImport() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M12 3v12M7 10l5 5 5-5" />
      <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
    </svg>
  );
}

function IconSync() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M20 12a8 8 0 0 1-14.5 4.6M4 12a8 8 0 0 1 14.5-4.6" />
      <path d="M20 4v4h-4M4 20v-4h4" />
    </svg>
  );
}

function IconWebhook() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M12 3a4 4 0 0 1 3.5 6l-3.5 6" />
      <path d="M6 21a4 4 0 0 1-1.5-7.7L8 15h8" />
      <path d="M18 21a4 4 0 0 0 1.5-7.7" />
      <circle cx="12" cy="15" r="1" />
    </svg>
  );
}
